import React, { useState, useEffect } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const RequestedLocalities = () => {
  const [statesData, setStatesData] = useState([]);
  const [cityData, setCityData] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [viewMode, setViewMode] = useState("pie");
  const [loading, setLoading] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [error, setError] = useState(null);

  // Enhanced gradient color palette for 2025 design trends
  const COLORS = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
    "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
    "linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)",
    "linear-gradient(135deg, #ff8a80 0%, #ea6100 100%)",
    "linear-gradient(135deg, #8360c3 0%, #2ebf91 100%)",
  ];

  const SOLID_COLORS = [
    "#667eea",
    "#f5576c",
    "#00f2fe",
    "#38f9d7",
    "#fee140",
    "#fed6e3",
    "#fecfef",
    "#fcb69f",
    "#ea6100",
    "#2ebf91",
    "#FF6B35",
    "#F7931E",
    "#FFD23F",
    "#06FFA5",
    "#118AB2",
    "#073B4C",
    "#EF476F",
    "#FFD166",
    "#06D6A0",
    "#B7094C",
  ];

  // Fetch states data on component mount
  useEffect(() => {
    fetchStatesData();
  }, []);

  const fetchStatesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(
        "https://api.medstown.com/customer/localities/stats?format=states"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStatesData(data);
    } catch (err) {
      console.error("Error fetching states data:", err);
      setError("Failed to load states data. Please try again later.");
      setStatesData([
        { name: "Telangana", value: 1425 },
        { name: "Maharashtra", value: 1163 },
        { name: "Delhi", value: 1133 },
        { name: "Uttar Pradesh", value: 952 },
        { name: "Karnataka", value: 885 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCityData = async (stateName) => {
    try {
      setLoadingCities(true);
      setError(null);
      const response = await fetch(
        `https://api.medstown.com/customer/localities/stats?format=city&value=${encodeURIComponent(
          stateName
        )}&country=India`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setCityData(data);
      setSelectedState(stateName);
    } catch (err) {
      console.error("Error fetching city data:", err);
      setError(`Failed to load data for ${stateName}. Please try again later.`);
      if (stateName === "Telangana") {
        setCityData([
          { name: "Hyderabad", value: 782 },
          { name: "Secunderabad", value: 148 },
          { name: "Warangal", value: 128 },
          { name: "Kazipet", value: 49 },
          { name: "Hanamkonda", value: 28 },
        ]);
        setSelectedState(stateName);
      }
    } finally {
      setLoadingCities(false);
    }
  };

  const currentData = selectedState ? cityData : statesData;
  const totalRequests = currentData.reduce((sum, item) => sum + item.value, 0);

  const renderCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage =
        totalRequests > 0 ? ((data.value / totalRequests) * 100).toFixed(1) : 0;
      return (
        <div className="ring-black/5 transform rounded-2xl border-0 bg-white/90 p-4 shadow-2xl ring-1 backdrop-blur-lg transition-all duration-200">
          <div className="mb-2 flex items-center space-x-3">
            <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
            <p className="text-lg font-bold text-gray-900">{data.name}</p>
          </div>
          <div className="space-y-1">
            <p className="font-semibold text-blue-600">
              Requests: {data.value.toLocaleString()}
            </p>
            <p className="text-emerald-600 font-medium">
              {percentage}% of total
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const handlePieClick = (data) => {
    if (!selectedState && data.name) {
      fetchCityData(data.name);
    }
  };

  const handleStateSelect = (stateName) => {
    fetchCityData(stateName);
  };

  const handleBackToStates = () => {
    setSelectedState(null);
    setCityData([]);
    setError(null);
  };

  const refreshData = () => {
    if (selectedState) {
      fetchCityData(selectedState);
    } else {
      fetchStatesData();
    }
  };

  if (loading) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -inset-10 opacity-50">
            <div className="absolute top-1/4 left-1/4 h-72 w-72 animate-pulse rounded-full bg-purple-300 mix-blend-multiply blur-xl filter"></div>
            <div className="animation-delay-2000 absolute top-1/3 right-1/4 h-72 w-72 animate-pulse rounded-full bg-yellow-300 mix-blend-multiply blur-xl filter"></div>
            <div className="animation-delay-4000 absolute bottom-1/4 left-1/3 h-72 w-72 animate-pulse rounded-full bg-pink-300 mix-blend-multiply blur-xl filter"></div>
          </div>
        </div>

        <div className="z-10 rounded-3xl border border-white/30 bg-white/20 p-12 text-center shadow-2xl backdrop-blur-lg">
          <div className="relative">
            <div className="border-transparent mx-auto mb-6 h-20 w-20 animate-spin rounded-full rounded-full border-4 bg-gradient-to-r from-blue-500 to-purple-600">
              <div className="absolute inset-2 rounded-full bg-white"></div>
            </div>
            <div className="absolute inset-0 animate-ping rounded-full bg-gradient-to-r from-blue-400 to-purple-500 opacity-75"></div>
          </div>
          <h2 className="text-transparent mb-2 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-2xl font-bold">
            Loading Dashboard
          </h2>
          <p className="text-gray-600">Fetching localities data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="from-slate-50 relative min-h-screen overflow-hidden bg-gradient-to-br via-blue-50 to-indigo-100">
      {/* Animated background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -inset-10 opacity-30">
          <div className="animate-float absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-blue-200 mix-blend-multiply blur-3xl filter"></div>
          <div className="animate-float-delayed absolute top-1/3 right-1/4 h-96 w-96 rounded-full bg-purple-200 mix-blend-multiply blur-3xl filter"></div>
          <div className="animate-float absolute bottom-1/4 left-1/2 h-96 w-96 rounded-full bg-pink-200 mix-blend-multiply blur-3xl filter"></div>
        </div>
      </div>

      <div className="relative z-10 p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          {/* Enhanced Header */}
          <div className="mb-12 text-center">
          

         
            <p className="mx-auto mb-6 max-w-2xl text-xl text-gray-600">
              {selectedState
                ? `Exploring cities in ${selectedState}`
                : "Discover request patterns across Indian states"}
            </p>

            {/* Stats Pills */}
            <div className="mb-6 flex flex-wrap justify-center gap-4">
              <div className="rounded-2xl border border-white/20 bg-white/60 px-6 py-3 shadow-lg backdrop-blur-lg">
                <div className="flex items-center space-x-3">
                  <div className="from-emerald-400 to-emerald-600 h-3 w-3 animate-pulse rounded-full bg-gradient-to-r"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Total Requests
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {totalRequests.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="rounded-2xl border border-white/20 bg-white/60 px-6 py-3 shadow-lg backdrop-blur-lg">
                <div className="flex items-center space-x-3">
                  <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"></div>
                  <span className="text-sm font-medium text-gray-600">
                    Locations
                  </span>
                  <span className="text-lg font-bold text-gray-900">
                    {currentData.length}
                  </span>
                </div>
              </div>

              <button
                onClick={refreshData}
                className="group rounded-2xl border border-white/20 bg-white/60 px-6 py-3 shadow-lg backdrop-blur-lg transition-all duration-200 hover:bg-white/80"
                disabled={loadingCities}
              >
                <div className="flex items-center space-x-2">
                  <svg
                    className={`h-4 w-4 text-blue-600 ${
                      loadingCities
                        ? "animate-spin"
                        : "transition-transform duration-300 group-hover:rotate-180"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    Refresh
                  </span>
                </div>
              </button>
            </div>

            {error && (
              <div className="mx-auto max-w-2xl rounded-2xl border border-red-200 bg-red-50/80 p-4 shadow-lg backdrop-blur-lg">
                <div className="flex items-center space-x-3">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500">
                    <svg
                      className="h-3 w-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </div>
                  <p className="font-medium text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Controls */}
          <div className="mb-12 flex flex-wrap justify-center gap-4">
            {selectedState && (
              <button
                onClick={handleBackToStates}
                className="group flex transform items-center space-x-3 rounded-2xl bg-gradient-to-r from-gray-600 to-gray-700 px-8 py-4 text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:from-gray-700 hover:to-gray-800 hover:shadow-xl"
              >
                <svg
                  className="h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span className="font-semibold">Back to States</span>
              </button>
            )}

            <div className="rounded-2xl border border-white/20 bg-white/60 p-2 shadow-lg backdrop-blur-lg">
              <div className="flex">
                <button
                  onClick={() => setViewMode("pie")}
                  className={`rounded-xl px-6 py-3 font-semibold transition-all duration-300 ${
                    viewMode === "pie"
                      ? "scale-105 transform bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                      : "text-gray-600 hover:bg-white/50"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                      />
                    </svg>
                    <span>Pie Chart</span>
                  </div>
                </button>
                <button
                  onClick={() => setViewMode("bar")}
                  className={`rounded-xl px-6 py-3 font-semibold transition-all duration-300 ${
                    viewMode === "bar"
                      ? "scale-105 transform bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg"
                      : "text-gray-600 hover:bg-white/50"
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <span>Bar Chart</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 gap-8">
            {/* Chart Section - Takes 3 columns */}
            <div className="xl:col-span-3">
              <div className="overflow-hidden rounded-3xl border border-white/20 bg-white/60 shadow-2xl backdrop-blur-xl">
                <div className="p-8">
                  <div className="mb-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {viewMode === "pie"
                        ? "Distribution Overview"
                        : "Performance Comparison"}
                    </h2>
                    {/* <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-ping"></div>
                      <span className="text-sm text-gray-600 font-medium">Live Data</span>
                    </div> */}
                  </div>

                  {loadingCities ? (
                    <div className="flex h-[500px] items-center justify-center">
                      <div className="text-center">
                        <div className="relative mb-6">
                          <div className="border-transparent mx-auto h-16 w-16 animate-spin rounded-full rounded-full border-4 bg-gradient-to-r from-blue-500 to-purple-600">
                            <div className="absolute inset-2 rounded-full bg-white"></div>
                          </div>
                          <div className="absolute inset-0 animate-ping rounded-full bg-gradient-to-r from-blue-400 to-purple-500 opacity-75"></div>
                        </div>
                        <p className="font-medium text-gray-600">
                          Loading {selectedState} data...
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="relative h-[500px]">
                      <ResponsiveContainer width="100%" height="100%">
                        {viewMode === "pie" ? (
                          <PieChart>
                            <Pie
                              data={currentData}
                              cx="50%"
                              cy="50%"
                              outerRadius={140}
                              innerRadius={60}
                              paddingAngle={3}
                              dataKey="value"
                              onClick={handlePieClick}
                              className="cursor-pointer"
                            >
                              {currentData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    SOLID_COLORS[index % SOLID_COLORS.length]
                                  }
                                  className="transition-opacity duration-300 hover:opacity-80 hover:brightness-110"
                                  stroke="white"
                                  strokeWidth={2}
                                />
                              ))}
                            </Pie>
                            <Tooltip content={renderCustomTooltip} />
                          </PieChart>
                        ) : (
                          <BarChart
                            data={currentData.slice(0, 12)}
                            margin={{
                              top: 20,
                              right: 30,
                              left: 20,
                              bottom: 100,
                            }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#f1f5f9"
                            />
                            <XAxis
                              dataKey="name"
                              angle={-45}
                              textAnchor="end"
                              height={120}
                              fontSize={12}
                              stroke="#64748b"
                            />
                            <YAxis stroke="#64748b" />
                            <Tooltip content={renderCustomTooltip} />
                            <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                              {currentData.slice(0, 12).map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    SOLID_COLORS[index % SOLID_COLORS.length]
                                  }
                                  className="transition-opacity duration-300 hover:opacity-80"
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        )}
                      </ResponsiveContainer>

                      {/* Enhanced Legend for Pie Chart */}
                      {viewMode === "pie" && (
                        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200/50 bg-white/90 backdrop-blur-sm">
                          <div className="flex max-h-32 flex-wrap justify-center gap-3 overflow-y-auto p-4">
                            {currentData.slice(0, 10).map((entry, index) => (
                              <div
                                key={entry.name}
                                className="flex items-center space-x-2 rounded-xl border border-gray-200/50 bg-gray-50/80 px-3 py-2"
                              >
                                <div
                                  className="h-3 w-3 rounded-full shadow-sm"
                                  style={{
                                    backgroundColor:
                                      SOLID_COLORS[index % SOLID_COLORS.length],
                                  }}
                                />
                                <span
                                  className="max-w-24 truncate text-xs font-medium text-gray-700"
                                  title={entry.name}
                                >
                                  {entry.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {entry.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Stats Section - Takes 1 column */}
          <div className="mt-8 space-y-6">
            {/* Top Performers */}
            <div className="overflow-hidden rounded-3xl border border-white/20 bg-white/60 shadow-xl backdrop-blur-xl">
              <div className="border-b border-white/20 bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-6">
                <h3 className="flex items-center text-xl font-bold text-gray-800">
                  <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-r from-blue-500 to-purple-600">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                  Top Performers
                </h3>
              </div>
              <div className="space-y-4 p-6">
                {currentData.slice(0, 5).map((item, index) => (
                  <div key={item.name} className="group relative">
                    <div className="hover:scale-102 flex items-center justify-between rounded-2xl border border-gray-200/30 bg-gradient-to-r from-gray-50/50 to-white/50 p-4 transition-all duration-300 hover:from-blue-50/50 hover:to-purple-50/50 hover:shadow-lg">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div
                            className="flex h-6 w-6 items-center justify-center rounded-xl text-xs font-bold text-white shadow-sm"
                            style={{
                              backgroundColor:
                                SOLID_COLORS[index % SOLID_COLORS.length],
                            }}
                          >
                            {index + 1}
                          </div>
                          {index === 0 && (
                            <div className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-yellow-400">
                              <svg
                                className="h-2 w-2 text-yellow-800"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 2L13.09 8.26L20 9.27L15 14.14L16.18 21.02L10 17.77L3.82 21.02L5 14.14L0 9.27L6.91 8.26L10 2Z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div>
                          <span
                            className="max-w-32 block truncate font-semibold text-gray-800"
                            title={item.name}
                          >
                            {item.name}
                          </span>
                          <span className="text-sm text-gray-500">
                            {totalRequests > 0
                              ? ((item.value / totalRequests) * 100).toFixed(1)
                              : 0}
                            % share
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-gray-900">
                          {item.value.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">requests</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* State Selection */}
            {!selectedState && (
              <div className="overflow-hidden rounded-3xl border border-white/20 bg-white/60 shadow-xl backdrop-blur-xl">
                <div className="border-b border-white/20 bg-gradient-to-r from-purple-500/10 to-pink-500/10 p-6">
                  <h3 className="flex items-center text-xl font-bold text-gray-800">
                    <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-r from-purple-500 to-pink-600">
                      <svg
                        className="h-4 w-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    Explore States
                  </h3>
                </div>
                <div className="p-6">
                  <div className="max-h-64 space-y-3 overflow-y-auto">
                    {statesData.map((state, index) => (
                      <button
                        key={state.name}
                        onClick={() => handleStateSelect(state.name)}
                        className="hover:scale-102 group flex w-full items-center justify-between rounded-2xl border border-gray-200/30 bg-gradient-to-r from-gray-50/50 to-white/50 p-4 transition-all duration-300 hover:from-blue-50/50 hover:to-purple-50/50 hover:shadow-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className="h-3 w-3 rounded-full"
                            style={{
                              backgroundColor:
                                SOLID_COLORS[index % SOLID_COLORS.length],
                            }}
                          />
                          <span className="font-medium text-gray-700 transition-colors duration-200 group-hover:text-blue-600">
                            {state.name}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-gray-900">
                            {state.value}
                          </span>
                          <svg
                            className="h-4 w-4 text-gray-400 transition-all duration-200 group-hover:translate-x-1 group-hover:text-blue-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="overflow-hidden rounded-3xl border border-white/20 bg-white/60 shadow-xl backdrop-blur-xl">
              <div className="from-emerald-500/10 border-b border-white/20 bg-gradient-to-r to-teal-500/10 p-6">
                <h3 className="flex items-center text-xl font-bold text-gray-800">
                  <div className="from-emerald-500 mr-3 flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-r to-teal-600">
                    <svg
                      className="h-4 w-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                  Analytics Summary
                </h3>
              </div>
              <div className="space-y-5 p-6">
                <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-3">
                  <span className="font-medium text-gray-600">
                    Total Locations
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {currentData.length}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-purple-50/50 to-pink-50/50 p-3">
                  <span className="font-medium text-gray-600">
                    Total Requests
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {totalRequests.toLocaleString()}
                  </span>
                </div>
                <div className="from-emerald-50/50 flex items-center justify-between rounded-xl bg-gradient-to-r to-teal-50/50 p-3">
                  <span className="font-medium text-gray-600">
                    Average per Location
                  </span>
                  <span className="text-xl font-bold text-gray-900">
                    {currentData.length > 0
                      ? Math.round(
                          totalRequests / currentData.length
                        ).toLocaleString()
                      : 0}
                  </span>
                </div>
                {currentData.length > 0 && (
                  <div className="flex items-center justify-between rounded-xl bg-gradient-to-r from-orange-50/50 to-red-50/50 p-3">
                    <span className="font-medium text-gray-600">
                      Top Location Share
                    </span>
                    <span className="text-xl font-bold text-gray-900">
                      {totalRequests > 0
                        ? (
                            (currentData[0].value / totalRequests) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }
        @keyframes float-delayed {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-30px) rotate(-180deg);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
          animation-delay: 2s;
        }
        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default RequestedLocalities;
